import * as ToggleMenuFlyout from '@ori-events/toggle-menu-flyout';
import * as UserAction from '@ori-events/user-action';
import { Clear, Search } from '@ori-ui/icons';
import { Backdrop, ButtonBase, SHADOW_LEVEL3, styled } from '@ori-ui/mui';
import useTestId from '@ori/testid-generator';
import type { ChangeEvent, KeyboardEvent, VFC } from 'react';
import { useCallback, useEffect } from 'react';
import { useDebounce, useThrottledCallback } from 'use-debounce';

import { DESKTOP_BREAKPOINT, SEARCH_THROTTLE_TIME, START_SEARCH } from '~/constants';
import { useNavigationContext } from '~/contexts/NavigationContext';
import { useWindowDimensionsContext } from '~/contexts/WindowDimensionsContext';
import { dispatchToggleMenu } from '~/helpers';
import { useMobileWidth, useTranslationByKey } from '~/hooks';
import { TranslationKeys } from '~/models';

const SearchInputRoot = styled('div')<{
  ownerState: { isVisible: boolean };
}>(({ ownerState, theme }) => ({
  background: theme.palette.common.white,
  boxSizing: 'border-box',
  height: 68,
  justifySelf: 'center',
  left: 0,
  marginLeft: 'auto',
  padding: theme.spacing(3, 0),
  position: 'fixed',
  top: 56,
  width: '100%',
  zIndex: theme.zIndex.speedDial,

  ...(ownerState.isVisible && {
    display: 'flex',
    justifyContent: 'center',
  }),

  [theme.breakpoints.up(DESKTOP_BREAKPOINT)]: {
    background: 'transparent',
    display: 'block',
    height: 38,
    marginLeft: 'auto',
    padding: theme.spacing(0, 0, 0, 3),
    position: 'static',
    width: 150,
    zIndex: 'unset',
  },

  [theme.breakpoints.up('lg')]: {
    marginLeft: 'auto',
    width: 185,
  },

  [theme.breakpoints.up('xl')]: {
    marginLeft: theme.spacing(6),
    width: 220,
  },
}));

const SearchInputBox = styled('div')<{
  ownerState: { isFocus: boolean };
}>(({ ownerState, theme }) => ({
  alignItems: 'center',
  background: theme.palette.grey[100],
  borderRadius: 18,
  boxSizing: 'border-box',
  color: theme.palette.grey[600],
  display: 'flex',
  fontSize: theme.typography.pxToRem(23),
  height: 38,
  paddingLeft: theme.spacing(3),
  width: 'calc(100% - 30px)',

  ...(ownerState.isFocus && {
    boxShadow: SHADOW_LEVEL3,
  }),

  [theme.breakpoints.up(DESKTOP_BREAKPOINT)]: {
    height: 38,
    visibility: 'visible',
    width: 150,
  },

  [theme.breakpoints.up('lg')]: {
    width: 185,
  },

  [theme.breakpoints.up('xl')]: {
    maxWidth: 220,
    width: 220,
  },
}));

const SearchInputInput = styled('input')(({ theme }) => ({
  background: theme.palette.grey[100],
  border: 'none',
  color: theme.palette.text.primary,
  fontSize: theme.typography.pxToRem(16),
  height: 20,
  marginLeft: 8,
  width: '100%',

  '&::placeholder': {
    color: theme.palette.text.primary,
  },

  [theme.breakpoints.up(DESKTOP_BREAKPOINT)]: {
    width: 65,
  },

  [theme.breakpoints.up('lg')]: {
    width: 95,
  },

  [theme.breakpoints.up('xl')]: {
    maxWidth: 135,
    width: 135,
  },
}));

const SearchInputClear = styled(ButtonBase)<{
  ownerState: { isFocus: boolean };
}>(({ ownerState, theme }) => ({
  height: 23,
  marginRight: theme.spacing(3),
  width: 23,

  [theme.breakpoints.up(DESKTOP_BREAKPOINT)]: {
    fontSize: theme.typography.pxToRem(20),
    marginLeft: theme.spacing(2),
    marginRight: 0,
    visibility: 'hidden',

    ...(ownerState.isFocus && {
      visibility: 'visible',
    }),
  },
}));

const onEnterPressHandler = (e: KeyboardEvent<HTMLInputElement>) => {
  if (e.key === 'Enter') {
    window.location.href = `/search?query=${(e.target as HTMLInputElement).value}`;
  }
};

export const SearchInput: VFC = () => {
  const translations = useTranslationByKey();
  const {
    isSearchInputFocus,
    isSearchInputVisible,
    setIsSearchInputFocus,
    getSearchResults,
    setSearchInputValue,
    searchInputValue,
    hideSearchResultsTab,
    clearSearchResults,
    startFetchingSearchData,
    toggleMenu,
    activeMenu,
  } = useNavigationContext();
  const { syncSizes } = useWindowDimensionsContext();
  const isMenuActive = activeMenu === ToggleMenuFlyout.MenuTypes.SearchResults;
  const isMobile = useMobileWidth();
  const ownerState = {
    isVisible: isSearchInputVisible,
    isFocus: isSearchInputFocus,
  };
  const { getTestId } = useTestId();

  const [debouncedValue] = useDebounce(searchInputValue, 350);

  const throttledAnalytics = useThrottledCallback(
    () => UserAction.dispatch(START_SEARCH),
    SEARCH_THROTTLE_TIME,
  );

  const onFocusHandler = useCallback(() => {
    setIsSearchInputFocus(true);
    syncSizes();
  }, [setIsSearchInputFocus, syncSizes]);

  const onChangeHandler = useCallback(
    ({ target: { value } }: ChangeEvent<HTMLInputElement>) => {
      const isValueEmpty = value.length === 0;

      if (isValueEmpty) {
        toggleMenu('');
        clearSearchResults();
      }

      setSearchInputValue(value);

      throttledAnalytics();

      if (!isValueEmpty && !isMenuActive) {
        dispatchToggleMenu(ToggleMenuFlyout.MenuTypes.SearchResults);
      }
      startFetchingSearchData();
    },
    [
      throttledAnalytics,
      clearSearchResults,
      isMenuActive,
      setSearchInputValue,
      startFetchingSearchData,
      toggleMenu,
    ],
  );

  const clearInput = useCallback(() => {
    setSearchInputValue('');
    clearSearchResults();
    toggleMenu('');
    setIsSearchInputFocus(false);
  }, [clearSearchResults, setIsSearchInputFocus, setSearchInputValue, toggleMenu]);

  const handleOnBlur = useCallback(() => {
    if (!searchInputValue) {
      setIsSearchInputFocus(false);
    }
    syncSizes();
  }, [searchInputValue, setIsSearchInputFocus, syncSizes]);

  useEffect(() => {
    getSearchResults(debouncedValue);
    // eslint-disable-next-line react-hooks/exhaustive-deps  -- we want to fetch results ONLY when input value changes (with debounce)
  }, [debouncedValue]);

  if (isMobile && !isSearchInputVisible) {
    return null;
  }

  return (
    <SearchInputRoot
      ownerState={ownerState}
      data-testid={getTestId('search-input-root')}
    >
      <SearchInputBox
        ownerState={ownerState}
        data-testid={getTestId('search-input-box')}
      >
        <Search
          fontSize="inherit"
          data-testid={getTestId('search')}
        />
        <SearchInputInput
          autoFocus={isMobile}
          type="text"
          placeholder={translations(TranslationKeys.Search)}
          value={searchInputValue}
          data-testid={getTestId('search-input')}
          onFocus={onFocusHandler}
          onChange={onChangeHandler}
          onBlur={handleOnBlur}
          onKeyDown={onEnterPressHandler}
        />
        <SearchInputClear
          disableRipple
          ownerState={ownerState}
          data-testid={getTestId('search-input-clear')}
          onClick={clearInput}
        >
          <Clear fontSize="inherit" />
        </SearchInputClear>
      </SearchInputBox>
      {isMobile && !isMenuActive ? (
        <Backdrop
          open
          sx={{ position: 'absolute', zIndex: '-1', top: 68, width: '100vw', height: '100vh' }}
          data-testid={getTestId('backdrop')}
          onClick={hideSearchResultsTab}
        />
      ) : null}
    </SearchInputRoot>
  );
};
